// generated by the plop

import { Injectable } from '@angular/core';
import { Action, NgxsOnInit, Selector, State, StateContext } from '@ngxs/store';
import { cloneDeep } from 'lodash';
import { switchMap } from 'rxjs';
import { PROGRESS_STATUSES } from 'symbols';
import { OrdersService } from '../../services/orders.service';
import { ORDER_LOCAL_STORAGE_KEY, OrderModel } from '../../symbols';
import { CreatedOrderByWS, UpdatedOrderByWS } from '../order-list/order-list.actions';
import {
  CreateOrder,
  CreateOrderFail,
  CreateOrderSuccess,
  DeleteLocalOrder,
  GetOrderById,
  GetOrderByIdFail,
  GetOrderByIdSuccess,
  IssuedOrder,
  IssuedOrderFail,
  IssuedOrderSuccess,
  SetCurrentOrderId,
  SetNotConfirmedOrder,
  SetNotConfirmedOrderFail,
  SetNotConfirmedOrderSuccess,
  UpdateLocalOrder,
  UpdateOrder,
  UpdateOrderFail,
  UpdateOrderSuccess
} from './order-manage.actions';
import { ORDER_MANAGE_STATE_DEFAULT, OrderManageStateModel } from './order-manage.model';

@State({ name: 'OrderManage', defaults: ORDER_MANAGE_STATE_DEFAULT })
@Injectable()
export class OrderManageState implements NgxsOnInit {

  constructor(private ordersService: OrdersService) {
  }

  ngxsOnInit(ctx: StateContext<any>) {
    const orders = localStorage.getItem(ORDER_LOCAL_STORAGE_KEY);
    if (orders) {
      ctx.patchState({
        orders: JSON.parse(orders)
      });
    }
  }

  @Selector()
  static realVersionOrders(state: OrderManageStateModel): OrderManageStateModel['realVersionOrders'] {
    return state.realVersionOrders;
  }

  @Selector()
  static updateOrderStatus(state: OrderManageStateModel): OrderManageStateModel['updateOrderStatus'] {
    return state.updateOrderStatus;
  }

  @Selector()
  static currentOrder(state: OrderManageStateModel): OrderModel {
    return state.orders?.[state.currentOrderId];
  }

  @Selector()
  static loadOrderStatus(state: OrderManageStateModel): PROGRESS_STATUSES {
    return state.loadOrderStatus;
  }

  @Action(GetOrderById)
  getOrderById(ctx: StateContext<OrderManageStateModel>, { id }: GetOrderById): void {
    ctx.patchState({
      loadOrderStatus: PROGRESS_STATUSES.IN_PROGRESS
    });
    if (ctx.getState().orders?.[id]?.id) {
      this.ordersService.getOrderById(id).subscribe({
        next: order => {
          if (order) {
            ctx.dispatch(new UpdatedOrderByWS(order));
          } else {
            ctx.patchState({
              loadOrderStatus: PROGRESS_STATUSES.SUCCESS
            });
          }
          ctx.patchState({
            currentOrderId: id
          });
        },
        error: err => ctx.dispatch(new GetOrderByIdFail(err))
      });
    } else {
      this.ordersService.getOrderById(id).subscribe({
        next: order => ctx.dispatch(new GetOrderByIdSuccess(order)),
        error: err => ctx.dispatch(new GetOrderByIdFail(err))
      });
    }
  }

  @Action(GetOrderByIdSuccess)
  getOrderByIdSuccess(ctx: StateContext<OrderManageStateModel>, { order }: GetOrderByIdSuccess): void {
    const orders = cloneDeep(ctx.getState().orders);
    orders[order.id] = order;
    ctx.patchState({
      orders,
      currentOrderId: order.id,
      loadOrderStatus: PROGRESS_STATUSES.SUCCESS
    });
  }

  @Action(GetOrderByIdFail)
  getOrderByIdFail(ctx: StateContext<OrderManageStateModel>, { error }: GetOrderByIdFail): void {
    ctx.patchState({
      loadOrderStatus: PROGRESS_STATUSES.FAIL
    });
  }

  @Action(SetCurrentOrderId)
  setCurrentOrderId(ctx: StateContext<OrderManageStateModel>, { id }: SetCurrentOrderId): void {
    ctx.patchState({
      currentOrderId: id
    });
  }

  @Action(UpdateLocalOrder)
  updateLocalOrder(ctx: StateContext<OrderManageStateModel>, { order }: UpdateLocalOrder): void {
    const orders = cloneDeep(ctx.getState().orders);
    orders[order.id] = { ...orders[order.id], ...order };
    ctx.patchState({
      orders
    });
    localStorage.setItem(ORDER_LOCAL_STORAGE_KEY, JSON.stringify(orders));
  }

  @Action(DeleteLocalOrder)
  deleteLocalOrder(ctx: StateContext<OrderManageStateModel>, { id }: DeleteLocalOrder): void {
    const orders = cloneDeep(ctx.getState().orders);
    const realVersionOrders = cloneDeep(ctx.getState().realVersionOrders);

    delete orders[id];
    delete realVersionOrders[id];
    ctx.patchState({
      orders,
      realVersionOrders
    });
  }


  @Action(UpdateOrder)
  updateOrder(ctx: StateContext<OrderManageStateModel>, { id, order, force }: UpdateOrder): void {
    ctx.patchState({
      updateOrderStatus: PROGRESS_STATUSES.IN_PROGRESS
    });

    this.ordersService.updateOrder(id, order, force).subscribe({
      next: updatedOrder => ctx.dispatch(new UpdateOrderSuccess(updatedOrder)),
      error: err => ctx.dispatch(new UpdateOrderFail(err))
    });
  }

  @Action(UpdateOrderSuccess)
  updateOrderSuccess(ctx: StateContext<OrderManageStateModel>, { order }: UpdateOrderSuccess): void {
    const orders = cloneDeep(ctx.getState().orders);
    ctx.patchState({
      updateOrderStatus: PROGRESS_STATUSES.SUCCESS,
      orders: { ...orders, [order.id]: { ...orders[order.id], version: order.version, debtId: order.debtId, debt: order.debt } }
    });
  }

  @Action([CreatedOrderByWS])
  createOrderByWS(ctx: StateContext<OrderManageStateModel>, { order }: CreatedOrderByWS): void {
    const orders = cloneDeep(ctx.getState().orders);
    const orderExist = orders[order.id];
    if (orderExist) {
      ctx.patchState({
        loadOrderStatus: PROGRESS_STATUSES.SUCCESS,
        orders: {
          ...orders,
          [order.id]: {
            ...orders[order.id],
            equipment: order.equipment,
            debt: order.debt,
            debtId: order.debtId
          }
        }
      });
    }
  }


  @Action([UpdatedOrderByWS])
  updateOrderByWS(ctx: StateContext<OrderManageStateModel>, { order }: UpdatedOrderByWS): void {
    const realVersionOrders = cloneDeep(ctx.getState().realVersionOrders);
    const orders = cloneDeep(ctx.getState().orders);
    const orderExist = orders[order.id];
    if (orderExist) {
      ctx.patchState({
        loadOrderStatus: PROGRESS_STATUSES.SUCCESS,
        orders: {
          ...orders,
          [order.id]: {
            ...orders[order.id],
            updater: order.updater,
            equipment: order.equipment,
            debt: order.debt,
            debtId: order.debtId
          }
        }
      });
    }
    ctx.patchState({
      loadOrderStatus: PROGRESS_STATUSES.SUCCESS,
      realVersionOrders: { ...realVersionOrders, [order.id]: order }
    });
  }

  @Action(CreatedOrderByWS)
  createdOrderByWS(ctx: StateContext<OrderManageStateModel>, { order }: CreatedOrderByWS): void {
    const orders = cloneDeep(ctx.getState().orders);
    ctx.patchState({
      orders: {
        ...orders,
        [order.id]: {
          ...orders[order.id],
          isChanged: false,
          isNew: false,
          updater: order.updater,
          client: order.client
        }
      }
    });
  }


  @Action(CreateOrder)
  createOrder(ctx: StateContext<OrderManageStateModel>, { order }: CreateOrder): void {
    ctx.patchState({
      updateOrderStatus: PROGRESS_STATUSES.IN_PROGRESS
    });

    this.ordersService.createOrder(order).subscribe({
      next: createdOrder => ctx.dispatch(new CreateOrderSuccess(createdOrder)),
      error: err => ctx.dispatch(new CreateOrderFail(err))
    });
  }

  @Action(CreateOrderSuccess)
  createOrderSuccess(ctx: StateContext<OrderManageStateModel>, { order }: CreateOrderSuccess): void {
    const orders = cloneDeep(ctx.getState().orders);
    ctx.patchState({
      updateOrderStatus: PROGRESS_STATUSES.SUCCESS,
      orders: { ...orders, [order.id]: { ...orders[order.id], debtId: order.debtId, debt: order.debt } }
    });
  }

  @Action(CreateOrderFail)
  createOrderFail(ctx: StateContext<OrderManageStateModel>, { error }: CreateOrderFail): void {
    ctx.patchState({
      updateOrderStatus: PROGRESS_STATUSES.FAIL
    });
  }

// added actions by plop
  @Action(IssuedOrder)
  issuedOrder(ctx: StateContext<OrderManageStateModel>, { orderId }: IssuedOrder): void {

    this.ordersService.issuedOrder(orderId).subscribe({
      next: res => ctx.dispatch(new IssuedOrderSuccess(res)),
      error: err => ctx.dispatch(new IssuedOrderFail(err))
    });
  }


  @Action(IssuedOrderFail)
  issuedOrderFail(ctx: StateContext<OrderManageStateModel>, { error }: IssuedOrderFail): void {
    console.error(error);
  }

// added actions by plop
  @Action(SetNotConfirmedOrder)
  setNotConfirmedOrder(ctx: StateContext<OrderManageStateModel>, { order }: SetNotConfirmedOrder): void {
    this.ordersService.updateOrder(order.id, order, false)
      .pipe(switchMap(order => this.ordersService.setNotConfirmedOrder(order.id)))
      .subscribe({
        next: res => ctx.dispatch(new SetNotConfirmedOrderSuccess(res)),
        error: err => ctx.dispatch(new SetNotConfirmedOrderFail(err))
      });
  }


}



